import { useApp } from '../context/AppContext';
import { trpc } from '../lib/trpc';
import { SeatCard } from '../components/SeatCard';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { SlotCalendar } from '../components/SlotCalendar';

export function SeatsScreen() {
    const { selectedDate, selectedBranchId } = useApp();
    const navigate = useNavigate();

    // Format date as YYYY-MM-DD for backend
    const dateStr = format(selectedDate, 'yyyy-MM-dd');

    const { data, isLoading } = trpc.listSeats.useQuery(
        { date: dateStr, branchId: selectedBranchId! },
        { enabled: !!selectedBranchId }
    );

    const [selectedSlot, setSelectedSlot] = useState<any>(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);

    const handleSlotClick = (seat: any, shift: any, booking?: any) => {
        if (booking) {
            // Open detail sheet
            setSelectedSlot({ ...booking, seat, shift });
            setIsDetailOpen(true);
        } else {
            // Navigate to create subscription
            navigate('/subscribe', {
                state: {
                    branchId: selectedBranchId,
                    seatId: seat.id,
                    shiftId: shift.id,
                    seatNumber: seat.seat_number,
                    shiftName: shift.name,
                    startDate: dateStr
                }
            });
        }
    };

    if (!selectedBranchId) return <div className="p-8 text-center text-muted-foreground">Please select a branch from the top bar.</div>;
    if (isLoading) return <div className="p-8 text-center text-muted-foreground">Loading seats...</div>;

    return (
        <div className="space-y-4 pb-20">
            {!data?.seats.length && (
                <div className="text-center p-8 text-muted-foreground">
                    No seats found in this branch.
                </div>
            )}
            {data?.seats.map(seat => (
                <SeatCard
                    key={seat.id}
                    seat={seat}
                    shifts={data.shifts}
                    bookings={data.bookings}
                    onSlotClick={handleSlotClick}
                />
            ))}

            <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
                <SheetContent side="bottom" className="rounded-t-xl h-[85vh] overflow-y-auto w-full max-w-none">
                    <SheetHeader>
                        <SheetTitle>Slot Details</SheetTitle>
                    </SheetHeader>
                    {selectedSlot && (
                        <div className="mt-6 space-y-8">
                            {/* Booking Info */}
                            <div>
                                <div className="text-sm text-muted-foreground mb-1">Booked By</div>
                                <div className="text-2xl font-bold">{selectedSlot.user_name}</div>
                                <div className="mt-4 grid grid-cols-2 gap-4">
                                    <div>
                                        <div className="text-sm text-muted-foreground">Status</div>
                                        <div className="font-medium capitalize flex items-center gap-2">
                                            <span className="h-2 w-2 rounded-full bg-green-500"></span>
                                            {selectedSlot.status}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-sm text-muted-foreground">Price</div>
                                        <div className="font-medium">--</div>
                                    </div>
                                </div>
                            </div>

                            {/* Calendar View */}
                            <div className="border-t pt-6">
                                <SlotCalendar
                                    branchId={selectedBranchId!}
                                    seatId={selectedSlot.seat.id}
                                    shift={selectedSlot.shift}
                                    seatNumber={selectedSlot.seat.seat_number}
                                />
                            </div>

                            <div className="p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                                Currently showing details for {selectedSlot.shift.name} shift on Seat {selectedSlot.seat.seat_number}.
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
}
